<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h3>{{ editingRecord ? "Edit" : "Add" }} Pregnancy Visit Record</h3>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveRecord">
          <div class="form-grid">
            <div class="form-group">
              <label>Visit Date *</label>
              <input
                type="date"
                v-model="form.visit_date"
                required
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Gestation (Weeks) *</label>
              <input
                type="number"
                v-model="form.gestation"
                min="4"
                max="42"
                required
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Weight (kg) *</label>
              <input
                type="number"
                v-model="form.weight"
                min="30"
                max="200"
                step="0.1"
                required
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Blood Pressure *</label>
              <div class="bp-inputs">
                <input
                  type="number"
                  v-model="form.bp_systolic"
                  placeholder="Systolic"
                  min="70"
                  max="200"
                  required
                  class="form-control"
                />
                <span class="bp-separator">/</span>
                <input
                  type="number"
                  v-model="form.bp_diastolic"
                  placeholder="Diastolic"
                  min="40"
                  max="130"
                  required
                  class="form-control"
                />
              </div>
            </div>

            <div class="form-group">
              <label>Mother's Height (cm)</label>
              <input
                type="number"
                v-model="form.mother_height"
                min="0"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Fundal Height (cm)</label>
              <input
                type="number"
                v-model="form.fundal_height"
                min="0"
                max="50"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Fetal Heart Rate (bpm)</label>
              <input
                type="number"
                v-model="form.fetal_heart_rate"
                min="60"
                max="200"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Presentation</label>
              <select v-model="form.presentation" class="form-control">
                <option value="">Select</option>
                <option value="Cephalic">Cephalic</option>
                <option value="Breech">Breech</option>
                <option value="Transverse">Transverse</option>
                <option value="Oblique">Oblique</option>
                <option value="Unengaged">Unengaged</option>
              </select>
            </div>

            <div class="form-group">
              <label>Urine Test</label>
              <select v-model="form.urine_test" class="form-control">
                <option value="">Select</option>
                <option value="Normal">Normal</option>
                <option value="Protein +">Protein +</option>
                <option value="Protein ++">Protein ++</option>
                <option value="Protein +++">Protein +++</option>
                <option value="Glucose +">Glucose +</option>
                <option value="Infection">Infection</option>
              </select>
            </div>

            <div class="form-group full-width">
              <label>Complaints/Symptoms</label>
              <textarea
                v-model="form.complaints"
                class="form-control"
                rows="3"
                placeholder="Any complaints, symptoms, or observations"
              ></textarea>
            </div>

            <div class="form-group full-width">
              <label>Examination Findings</label>
              <textarea
                v-model="form.examination"
                class="form-control"
                rows="3"
                placeholder="Detailed examination findings"
              ></textarea>
            </div>

            <div class="form-group full-width">
              <label>Management/Advice</label>
              <textarea
                v-model="form.management"
                class="form-control"
                rows="3"
                placeholder="Management plan and advice given"
              ></textarea>
            </div>

            <div class="form-group">
              <label>Next Visit Date</label>
              <input
                type="date"
                v-model="form.next_visit_date"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label>Seen By</label>
              <input
                type="text"
                v-model="form.seen_by"
                class="form-control"
                placeholder="Doctor/Midwife name"
              />
            </div>
          </div>

          <div class="form-preview">
            <div class="preview-item">
              <span>BP Classification:</span>
              <strong :class="getBPClass(form.bp_systolic, form.bp_diastolic)">
                {{ classifyBP(form.bp_systolic, form.bp_diastolic) }}
              </strong>
            </div>
            <div class="preview-item">
              <span>FHR Status:</span>
              <strong :class="getFHRClass(form.fetal_heart_rate)">
                {{ classifyFHR(form.fetal_heart_rate) }}
              </strong>
            </div>
            <div v-if="form.fundal_height" class="preview-item">
              <span>Fundal Height vs Gestation:</span>
              <strong>{{ assessFundalHeight() }}</strong>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{
                saving
                  ? "Saving..."
                  : (editingRecord ? "Update" : "Save") + " Record"
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "PregnancyVisitModal",
  props: {
    visitId: {
      type: [Number, String],
      default: null,
    },
    patientId: {
      type: [Number, String],
      default: null,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    editRecord: {
      type: Object,
      default: null,
    },
    lastGestation: {
      type: Number,
      default: null,
    },
    lastWeight: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      saving: false,
      editingRecord: null,
      form: {
        visit_date: new Date().toISOString().split("T")[0],
        gestation: null,
        weight: null,
        bp_systolic: null,
        bp_diastolic: null,
        mother_height: null,
        fundal_height: "",
        fetal_heart_rate: "",
        presentation: "",
        urine_test: "",
        complaints: "",
        examination: "",
        management: "",
        next_visit_date: "",
        seen_by: "",
      },
    };
  },
  created() {
    if (this.editRecord) {
      this.editingRecord = this.editRecord;
      this.form = {
        visit_date: this.editRecord.visit_date || "",
        gestation: this.editRecord.gestation || "",
        weight: this.editRecord.weight || "",
        bp_systolic: this.editRecord.bp_systolic || "",
        bp_diastolic: this.editRecord.bp_diastolic || "",
        mother_height: this.editRecord.mother_height || "",
        fundal_height: this.editRecord.fundal_height || "",
        fetal_heart_rate: this.editRecord.fetal_heart_rate || "",
        presentation: this.editRecord.presentation || "",
        urine_test: this.editRecord.urine_test || "",
        complaints: this.editRecord.complaints || "",
        examination: this.editRecord.examination || "",
        management: this.editRecord.management || "",
        next_visit_date: this.editRecord.next_visit_date || "",
        seen_by: this.editRecord.seen_by || "",
      };
    } else {
      this.resetForm();
    }
  },
  methods: {
    resetForm() {
      this.form = {
        visit_date: new Date().toISOString().split("T")[0],
        gestation: this.lastGestation,
        weight: this.lastWeight,
        bp_systolic: "",
        bp_diastolic: "",
        mother_height: "",
        fundal_height: "",
        fetal_heart_rate: "",
        presentation: "",
        urine_test: "",
        complaints: "",
        examination: "",
        management: "",
        next_visit_date: "",
        seen_by: "",
      };
    },

    closeModal() {
      this.$emit("close");
    },

    async saveRecord() {
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          visit_id: this.visitId,
        };

        let response;
        if (this.editingRecord) {
          response = await generalApiRequest(
            `anc_pregnancy_data/${this.editingRecord.id}`,
            "PUT",
            payload,
            1,
          );
        } else {
          console.log("=======================");

          console.log(payload);

          response = await generalApiRequest(
            "anc_pregnancy_data",
            "POST",
            payload,
            1,
          );
        }

        if (response.success) {
          this.$emit("saved", response.data);
          this.closeModal();
        } else {
          alert(response.message || "Failed to save record");
        }
      } catch (error) {
        console.error("Error saving pregnancy data:", error);
        alert("An error occurred while saving the record");
      } finally {
        this.saving = false;
      }
    },

    getBPClass(systolic, diastolic) {
      if (!systolic || !diastolic) return "";
      if (systolic >= 140 || diastolic >= 90) return "bp-high";
      if (systolic >= 130 || diastolic >= 85) return "bp-elevated";
      if (systolic < 90 || diastolic < 60) return "bp-low";
      return "bp-normal";
    },

    getFHRClass(fhr) {
      if (!fhr) return "";
      if (fhr < 110) return "fhr-low";
      if (fhr > 160) return "fhr-high";
      return "fhr-normal";
    },

    classifyBP(systolic, diastolic) {
      if (!systolic || !diastolic) return "Not recorded";
      if (systolic >= 140 || diastolic >= 90) return "Hypertensive";
      if (systolic >= 130 || diastolic >= 85) return "Elevated";
      if (systolic < 90 || diastolic < 60) return "Low";
      return "Normal";
    },

    classifyFHR(fhr) {
      if (!fhr) return "Not recorded";
      if (fhr < 110) return "Bradycardia";
      if (fhr > 160) return "Tachycardia";
      return "Normal";
    },

    assessFundalHeight() {
      if (!this.form.fundal_height || !this.form.gestation) return "";
      const expected = this.form.gestation;
      const actual = this.form.fundal_height;
      const difference = Math.abs(actual - expected);

      if (difference <= 2) return "Appropriate for dates";
      if (actual > expected) return "Large for dates";
      return "Small for dates";
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.mmodal-content {
  background: white;
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.close {
  background: rgb(233, 22, 22);
  border: 1px solid rgb(182, 178, 178);
  font-size: 1.5rem;
  color: white;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  padding: 30px;
  overflow-y: auto;
  flex-grow: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.bp-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bp-inputs .form-control {
  flex: 1;
}

.bp-separator {
  font-weight: bold;
  color: #495057;
}

.form-preview {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.preview-item {
  text-align: center;
}

.preview-item span {
  display: block;
  color: #7f8c8d;
  font-size: 14px;
  margin-bottom: 5px;
}

.preview-item strong {
  font-size: 16px;
  color: #2c3e50;
}

.bp-normal {
  color: #27ae60;
  font-weight: 600;
}

.bp-elevated {
  color: #f39c12;
  font-weight: 600;
}

.bp-high {
  color: #e74c3c;
  font-weight: 600;
}

.bp-low {
  color: #3498db;
  font-weight: 600;
}

.fhr-normal {
  color: #27ae60;
  font-weight: 600;
}

.fhr-low,
.fhr-high {
  color: #e74c3c;
  font-weight: 600;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.btn {
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 1200px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .bp-inputs {
    flex-direction: column;
    gap: 5px;
  }

  .bp-separator {
    display: none;
  }
}
</style>
